import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import useHolHostSession from "./useHolHostSession";
import { registerNavigationGuard } from "../../../utils/adminNavigationGuard";
import SpectatorOnOffButton from "../SpectatorOnOffButton";
import HolHostStepper from "./hol/HolHostStepper";
import HolIntroStep from "./hol/HolIntroStep";
import HolHowToPlayStep from "./hol/HolHowToPlayStep";
import HolStartingStep from "./hol/HolStartingStep";
import HolLiveDashboard from "./hol/HolLiveDashboard";
import HolFinalResults from "./hol/HolFinalResults";
import HolConfirmDialog from "./hol/HolConfirmDialog";
import { HOL, holPrimaryBtnSx } from "../../UserSide/pages/holTheme";
import HolSessionCountdown from "../../games/HolSessionCountdown";

const setupModeBtnSx = { textTransform: "none", fontWeight: 700, borderRadius: "10px" };

const getEndGameNote = (session) => {
  if (!session?.hot_seat_device_id) return null;
  const rc = session.revealed_count || 0;
  const total = session.total_cards || 0;
  if (rc < 1 || rc >= total) return null;
  const name = session.hot_seat_display_name || "The hot seat player";
  const played = rc - 1;
  const pendingNote = session.pending_user_guess ? " The locked call that hasn't been revealed is dropped." : "";
  if (played < 1) return `${name} hasn't resolved a call yet, so this run won't be scored.${pendingNote}`;
  return `${name}'s score so far (${session.score || 0} / ${played}) will be locked in.${pendingNote}`;
};

/**
 * Play with Host (Higher or Lower): Prep Game (force join + spectator on),
 * then Start → How to Play → Start Session, the live host dashboard, and final results.
 */
const HigherOrLowerSpHostTab = () => {
  const {
    loading,
    busy,
    error,
    setError,
    session,
    joiners,
    leaderboard,
    numberOfCards,
    forceActive,
    hotSeatAnnounce,
    closeHotSeatAnnounce,
    clueLoading,
    countdownEndsAt,
    clearCountdown,
    finalResults,
    clearFinalResults,
    refresh,
    beginPrepMusic,
    stopHostMusic,
    action,
    startSession,
    setClueUnlocked,
    setLobbyPhase,
    returnSpectateToStart,
    restartSession,
    endGame,
    forceJoin,
    forceStop,
    playClue,
  } = useHolHostSession();

  const [prepared, setPrepared] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [startState, setStartState] = useState(null);
  const [autoSpectatorSessionId, setAutoSpectatorSessionId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const mountedRef = useRef(true);
  const setupSpectatorRef = useRef(null);
  const clueSetupRef = useRef({ clue_max_plays: undefined, clue_user_can_play: false });
  const sessionOpen = !!session?.id;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Players mirror this setup step until the live session exists.
  useEffect(() => {
    if (sessionOpen || startState) return undefined;
    setLobbyPhase(setupStep === 0 ? "intro" : "rules");
    return undefined;
  }, [sessionOpen, startState, setupStep, setLobbyPhase]);

  useEffect(() => () => setLobbyPhase("intro"), [setLobbyPhase]);

  useEffect(() => {
    if (!sessionOpen) return undefined;
    const unregister = registerNavigationGuard((proceed) => {
      setConfirm({ kind: "leave", proceed });
      return true;
    });
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      unregister();
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [sessionOpen]);

  const runStart = useCallback(async (opts) => {
    if (opts) clueSetupRef.current = opts;
    setError(null);
    setStartState({ progress: 0, failed: false });
    const data = await startSession(clueSetupRef.current);
    if (!mountedRef.current) return;
    if (!data?.success) {
      setStartState({ progress: 0, failed: true });
      return;
    }
    setAutoSpectatorSessionId(data.session_id || null);
    setStartState(null);
  }, [startSession, setError]);

  const handleAutoSpectatorHandled = useCallback(() => setAutoSpectatorSessionId(null), []);

  const handlePrepGame = async () => {
    if (preparing) return;
    setPreparing(true);
    setError(null);
    try {
      // Start in this click so the browser allows playback. Stop again if prep fails.
      await beginPrepMusic();
      if (!forceActive) {
        const result = await forceJoin();
        if (!mountedRef.current || result === null) {
          stopHostMusic();
          return;
        }
      }
      await setupSpectatorRef.current?.ensureOn(null);
      if (!mountedRef.current) {
        stopHostMusic();
        return;
      }
      setPrepared(true);
      setSetupStep(0);
    } finally {
      if (mountedRef.current) setPreparing(false);
    }
  };

  const handleConfirmEndGame = async () => {
    const leaveAfter = confirm?.kind === "leave" ? confirm.proceed : null;
    setConfirm(null);
    const results = await endGame();
    if (!results) return;
    setSetupStep(0);
    if (leaveAfter) {
      clearFinalResults();
      leaveAfter();
    }
  };

  const handleConfirmRestart = async () => {
    setConfirm(null);
    const data = await restartSession(clueSetupRef.current);
    if (data?.success) setAutoSpectatorSessionId(data.session_id || null);
  };

  const handleLeaveWithoutEnding = () => {
    const proceed = confirm?.proceed;
    setConfirm(null);
    proceed?.();
  };

  const handleResultsDone = () => {
    clearFinalResults();
    setPrepared(false);
    setSetupStep(0);
    returnSpectateToStart();
  };

  if (loading && !session && !finalResults) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress sx={{ color: HOL.gold }} />
      </Box>
    );
  }

  let content;
  if (finalResults) {
    content = <HolFinalResults results={finalResults} onDone={handleResultsDone} />;
  } else if (startState) {
    content = (
      <>
        <HolHostStepper activeStep={startState.progress >= 3 ? 3 : 2} />
        <HolStartingStep
          numberOfCards={numberOfCards}
          progress={startState.progress}
          error={startState.failed ? error || "Couldn't start the session. Please try again." : null}
          onRetry={runStart}
          onBack={() => {
            setError(null);
            setStartState(null);
            setSetupStep(1);
          }}
        />
      </>
    );
  } else if (session) {
    content = (
      <HolLiveDashboard
        session={session}
        joiners={joiners}
        leaderboard={leaderboard}
        busy={busy}
        forceActive={forceActive}
        clueLoading={clueLoading}
        onAction={action}
        onForceJoin={forceJoin}
        onForceStop={forceStop}
        onRefresh={refresh}
        onPlayClue={playClue}
        onSetClueUnlocked={setClueUnlocked}
        onRequestEndGame={() => setConfirm({ kind: "end" })}
        onRequestRestart={() => setConfirm({ kind: "restart" })}
        hotSeatAnnounce={hotSeatAnnounce}
        onCloseHotSeatAnnounce={closeHotSeatAnnounce}
        autoSpectatorSessionId={autoSpectatorSessionId}
        onAutoSpectatorHandled={handleAutoSpectatorHandled}
      />
    );
  } else if (!prepared) {
    content = (
      <Box sx={{ display: "flex", justifyContent: "center", py: { xs: 8, sm: 12 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={handlePrepGame}
          disabled={busy || preparing}
          autoFocus
          sx={{ ...holPrimaryBtnSx, px: { xs: 5, sm: 8 }, fontSize: { xs: "1.05rem", sm: "1.2rem" } }}
        >
          {preparing || busy ? "Preparing…" : "Prep Game"}
        </Button>
      </Box>
    );
  } else {
    content = (
      <>
        <HolHostStepper activeStep={setupStep} />
        {setupStep === 0 ? (
          <HolIntroStep numberOfCards={numberOfCards} onNext={() => setSetupStep(1)} />
        ) : (
          <HolHowToPlayStep
            numberOfCards={numberOfCards}
            onBack={() => setSetupStep(0)}
            onStartSession={runStart}
            busy={busy}
          />
        )}
      </>
    );
  }

  const endGameNote = getEndGameNote(session);
  const liveOpen = !finalResults && !startState && !!session;
  const showPrep = !liveOpen && !finalResults && !startState && !prepared;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {!liveOpen ? (
        <Box
          sx={{
            display: showPrep ? "none" : "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          {forceActive ? (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              disabled={busy}
              onClick={forceStop}
              sx={setupModeBtnSx}
            >
              Force Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              disabled={busy}
              onClick={forceJoin}
              sx={setupModeBtnSx}
            >
              Force Join (MYM)
            </Button>
          )}
          <SpectatorOnOffButton
            ref={setupSpectatorRef}
            gameType="higher_or_lower"
            gameLabel="Higher or Lower"
            sessionId={session?.id || null}
            disabled={busy}
            size="small"
          />
        </Box>
      ) : null}

      {error && !startState?.failed ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {content}

      {countdownEndsAt ? (
        <HolSessionCountdown endsAt={countdownEndsAt} onDone={clearCountdown} />
      ) : null}

      <HolConfirmDialog
        open={confirm?.kind === "end"}
        title="End the game?"
        confirmLabel="End Game"
        cancelLabel="Continue Game"
        onConfirm={handleConfirmEndGame}
        onCancel={() => setConfirm(null)}
        busy={busy}
      >
        This stops the session for everyone and closes the join queue. Players see GAME OVER followed by the final
        leaderboard.
        {endGameNote ? (
          <Box component="span" sx={{ display: "block", mt: 1.25, color: HOL.gold }}>
            {endGameNote}
          </Box>
        ) : null}
      </HolConfirmDialog>

      <HolConfirmDialog
        open={confirm?.kind === "restart"}
        tone="primary"
        title="Restart the session?"
        confirmLabel="Restart"
        onConfirm={handleConfirmRestart}
        onCancel={() => setConfirm(null)}
        busy={busy}
      >
        A fresh session starts with a new deck. Players in the queue stay, but the current run and this
        session&apos;s leaderboard are cleared, and no final results are shown.
      </HolConfirmDialog>

      <HolConfirmDialog
        open={confirm?.kind === "leave"}
        title="Leave Game?"
        confirmLabel="End Game"
        cancelLabel="Continue Game"
        onConfirm={handleConfirmEndGame}
        onCancel={() => setConfirm(null)}
        busy={busy}
        footer={
          <Button
            size="small"
            onClick={handleLeaveWithoutEnding}
            sx={{ textTransform: "none", color: HOL.muted, fontWeight: 600 }}
          >
            Leave without ending
          </Button>
        }
      >
        The current HoL session is still active. Are you sure you want to leave?
        {endGameNote ? (
          <Box component="span" sx={{ display: "block", mt: 1.25, color: HOL.muted, fontSize: "0.85rem" }}>
            Ending now: {endGameNote}
          </Box>
        ) : null}
      </HolConfirmDialog>
    </Box>
  );
};

export default HigherOrLowerSpHostTab;
